import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { TableAction } from '@/components';
import { useFetchAdvanceBatchOrders } from '@/hooksApi';
import { AdvanceBatchOrder } from '@/types';
import { formatWithThousandSeparator } from '@/utils';

const PAGE_SIZE = 25;

const CHIP_BASE =
  'inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-solid text-[12px] font-semibold transition-colors duration-150 cursor-pointer whitespace-nowrap';
const CHIP_INACTIVE =
  'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:border-[rgb(200,200,196)] hover:bg-[rgb(245,245,243)]';
const CHIP_ACTIVE = 'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]';

const statusColors: Record<string, { bg: string; text: string }> = {
  delivered: { bg: 'hsla(152,60%,48%,0.12)', text: 'hsl(152,60%,38%)' },
  returned: { bg: 'hsla(340,50%,52%,0.12)', text: 'hsl(340,45%,42%)' },
};

interface OrderRecord extends AdvanceBatchOrder {
  key: string;
}

const getOrderColumns = (t: (key: string) => string): ColumnsType<OrderRecord> => [
  {
    title: t('order'),
    key: 'order',
    render: (_: unknown, record) => (
      <div className='flex min-w-0 flex-col gap-0.5'>
        <span className='font-mono text-[13px] font-bold text-[rgb(57,57,60)]'>
          <bdi>{record.order_platform_code ?? '—'}</bdi>
        </span>
        {record.excluded && record.exclusion_reason ? (
          <span className='text-[11px] italic text-[rgb(115,123,140)]'>
            {record.exclusion_reason}
          </span>
        ) : null}
      </div>
    ),
  },
  {
    title: t('status'),
    key: 'status',
    render: (_: unknown, record) => {
      const color = record.reported_status ? statusColors[record.reported_status] : undefined;
      return record.excluded ? (
        <span className='text-[11px] text-[rgb(115,123,140)]'>
          {String(t('advance_cod_excluded_label'))}
        </span>
      ) : color && record.reported_status ? (
        <span
          className='inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold'
          style={{ backgroundColor: color.bg, color: color.text }}
        >
          {String(t(`advance_cod_order_status.${record.reported_status}`))}
        </span>
      ) : (
        <span className='text-[11px] text-[rgb(115,123,140)]'>
          {String(t('advance_cod_order_status.awaiting'))}
        </span>
      );
    },
  },
  {
    title: t('amount'),
    key: 'amount',
    align: 'end' as const,
    render: (_: unknown, record) => {
      const amount = Number(record.reported_amount_collected ?? record.items_amount ?? 0);
      return (
        <span className='whitespace-nowrap text-[13px] font-medium text-[rgb(57,57,60)]'>
          <bdi>
            {formatWithThousandSeparator(amount)} {String(t('iqd'))}
          </bdi>
        </span>
      );
    },
  },
];

interface BatchOrdersPaginatedProps {
  uid: string;
  /** excluded rows in the whole batch — drives the Excluded chip visibility */
  excludedCount: number;
  /** pre-filter to excluded rows ("View excluded orders", QA defect 18) */
  excludedOnly: boolean;
  onExcludedOnlyChange: (excludedOnly: boolean) => void;
}

/**
 * Paginated financed-orders table on the batch detail (session-12 rework) —
 * batches can hold 500 orders, so the rows come from the server-paginated
 * GET /batches/{uid}/orders endpoint through the SAME house TableAction +
 * pagination pattern as the Orders/Shipments lists. Never renders the whole
 * batch inline.
 */
function BatchOrdersPaginated({
  uid,
  excludedCount,
  excludedOnly,
  onExcludedOnlyChange,
}: BatchOrdersPaginatedProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isFetching } = useFetchAdvanceBatchOrders(
    uid,
    currentPage,
    PAGE_SIZE,
    excludedOnly,
  );

  const records: OrderRecord[] = (data?.object?.items ?? []).map((order, index) => ({
    ...order,
    key: order.order_platform_code ?? `${currentPage}-${index}`,
  }));

  const handleFilterChange = (nextExcludedOnly: boolean) => {
    if (nextExcludedOnly !== excludedOnly) {
      onExcludedOnlyChange(nextExcludedOnly);
      setCurrentPage(1);
    }
  };

  return (
    <div className='flex flex-col gap-3'>
      {excludedCount > 0 ? (
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => handleFilterChange(false)}
            className={`${CHIP_BASE} ${excludedOnly ? CHIP_INACTIVE : CHIP_ACTIVE}`}
          >
            {String(t('all'))}
          </button>
          <button
            type='button'
            onClick={() => handleFilterChange(true)}
            className={`${CHIP_BASE} ${excludedOnly ? CHIP_ACTIVE : CHIP_INACTIVE}`}
            data-testid='batch-orders-excluded-chip'
          >
            {String(t('advance_cod_excluded_label'))}
            <span className='ms-0.5 rounded-full bg-[rgba(237,224,74,0.25)] px-1.5 text-[10px] text-[rgb(57,57,60)]'>
              {excludedCount}
            </span>
          </button>
        </div>
      ) : null}
      <TableAction
        columns={getOrderColumns(t as (key: string) => string)}
        data={records}
        pageSize={PAGE_SIZE}
        current={currentPage}
        total={data?.object?.total ?? 0}
        loading={isFetching}
        rowSelectionEnabled={false}
        onPaginationChange={(page) => setCurrentPage(page)}
        rowClassName={(record: OrderRecord) => (record.excluded ? 'opacity-60' : '')}
      />
    </div>
  );
}

export default BatchOrdersPaginated;
